import {
  ArrowUpRight,
  Bot,
  Building2,
  Camera,
  CircuitBoard,
  Globe2,
  Handshake,
  Network,
  ShieldCheck,
  Sparkles,
  Store,
  UsersRound,
  WalletCards,
  Zap
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Language = 'en' | 'zh';
export type SiteKey = 'group' | 'enring' | 'minimall';

export type Localized = Record<Language, string>;

export interface Feature {
  icon: LucideIcon;
  title: Localized;
  body: Localized;
}

export interface Unit {
  key: 'enring' | 'minimall' | 'sentribee';
  name: string;
  title: Localized;
  body: Localized;
  href: string;
  external?: boolean;
  tone: string;
  keywords: string[];
  icon: LucideIcon;
}

export interface SiteContent {
  key: SiteKey;
  host: string;
  name: string;
  eyebrow: Localized;
  title: Localized;
  lead: Localized;
  metaTitle: Localized;
  metaDescription: Localized;
  cta: Localized;
  secondaryCta?: Localized;
  features: Feature[];
  proof: Feature[];
  focus: Localized;
}

export const languageLabels: Record<Language, string> = {
  en: 'English',
  zh: '简体中文'
};

export const groupUnits: Unit[] = [
  {
    key: 'enring',
    name: 'Enring',
    title: {
      en: 'Merchant Payment Capability',
      zh: '商户支付能力'
    },
    body: {
      en: 'Merchant payment acceptance, WeChat Pay enablement, settlement-related support and cross-border payment operations.',
      zh: '提供商户收款、微信支付接入、结算相关支持与跨境支付运营能力。'
    },
    href: 'https://enring.co.nz/',
    tone: 'From operational foundations',
    keywords: ['Merchant Services', 'Settlement Infrastructure', 'Cross-Border Commerce'],
    icon: WalletCards
  },
  {
    key: 'minimall',
    name: 'MINI MALL',
    title: {
      en: 'Community Commerce Network',
      zh: '社区商业网络'
    },
    body: {
      en: 'A private-domain community commerce network connecting merchants, agents, promoters, cashback incentives and repeatable growth loops.',
      zh: '连接商户、代理、推广者、返现激励与可复制增长链路的私域社区商业网络。'
    },
    href: 'https://mmall.co.nz/',
    tone: 'To community-driven scale',
    keywords: ['Community Commerce', 'Private-Domain Network', 'Cashback System'],
    icon: UsersRound
  },
  {
    key: 'sentribee',
    name: 'Sentribee AI',
    title: {
      en: 'Operational Intelligence Platform',
      zh: '运营智能平台'
    },
    body: {
      en: 'Edge AI, computer vision, sensors and automation systems for field operations and industry workflows.',
      zh: '面向现场运营与行业流程的边缘 AI、计算机视觉、传感器与自动化系统。'
    },
    href: 'https://sentribee.ai/',
    external: true,
    tone: 'Toward operational intelligence',
    keywords: ['Edge AI', 'Computer Vision', 'Operational Intelligence'],
    icon: Bot
  }
];

export const sites: Record<SiteKey, SiteContent> = {
  group: {
    key: 'group',
    host: 'mmallgroup.com',
    name: 'MINI MALL GROUP',
    eyebrow: {
      en: 'New Zealand Technology Group',
      zh: '新西兰科技集团'
    },
    title: {
      en: 'Building the operating layer for commerce, communities and applied intelligence.',
      zh: '构建连接商业、社群与应用智能的运营基础层。'
    },
    lead: {
      en: 'MINI MALL GROUP connects commerce infrastructure, merchant networks, community commerce and AI-powered operational systems.',
      zh: 'MINI MALL GROUP 连接商业基础设施、商户网络、社区商业与 AI 驱动的运营系统。'
    },
    metaTitle: {
      en: 'MINI MALL GROUP | Commerce Infrastructure, Community Commerce and Operational Intelligence',
      zh: 'MINI MALL GROUP | 商业基础设施、社区商业与运营智能'
    },
    metaDescription: {
      en: 'MINI MALL GROUP is a New Zealand technology group connecting commerce infrastructure, merchant networks, community commerce and AI-powered operational systems.',
      zh: 'MINI MALL GROUP 是一家新西兰科技集团，连接商业基础设施、商户网络、社区商业与 AI 驱动的运营系统。'
    },
    cta: {
      en: 'Explore the group',
      zh: '了解集团架构'
    },
    secondaryCta: {
      en: 'Contact us',
      zh: '联系合作'
    },
    focus: {
      en: 'Corporate platform',
      zh: '集团平台'
    },
    features: [
      {
        icon: Building2,
        title: { en: 'Commerce Infrastructure', zh: '商业基础设施' },
        body: {
          en: 'Operational support, merchant services and settlement capability for local and cross-border commerce.',
          zh: '面向本地及跨境商业的运营支持、商户服务与结算能力。'
        }
      },
      {
        icon: Network,
        title: { en: 'Merchant & Community Network', zh: '商户与社区网络' },
        body: {
          en: 'Trusted distribution and merchant growth through agents, promoters, cashback programs and private communities.',
          zh: '通过代理、推广者、返现机制与私域社群，形成可信分销与商户增长网络。'
        }
      },
      {
        icon: CircuitBoard,
        title: { en: 'Operational Intelligence', zh: '运营智能' },
        body: {
          en: 'Edge AI, sensors, computer vision and automation for measurable field and operational workflows.',
          zh: '通过边缘 AI、传感器、计算机视觉与自动化，服务可度量的现场与运营流程。'
        }
      }
    ],
    proof: [
      {
        icon: Sparkles,
        title: { en: 'Past', zh: '过去' },
        body: { en: 'Enring established the group’s commerce and settlement foundation.', zh: 'Enring 奠定集团的商业与结算基础。' }
      },
      {
        icon: Store,
        title: { en: 'Present', zh: '现在' },
        body: { en: 'MINI MALL expands the group into private-domain community commerce and merchant growth.', zh: 'MINI MALL 将集团能力扩展到私域社区商业与商户增长。' }
      },
      {
        icon: Bot,
        title: { en: 'Future', zh: '未来' },
        body: { en: 'Sentribee AI applies operational intelligence to industry workflows.', zh: 'Sentribee AI 将运营智能应用到行业流程。' }
      }
    ]
  },
  enring: {
    key: 'enring',
    host: 'enring.co.nz',
    name: 'Enring',
    eyebrow: {
      en: 'Merchant Payment Capability',
      zh: '商户支付能力'
    },
    title: {
      en: 'Merchant payment acceptance and WeChat Pay service in New Zealand.',
      zh: '面向新西兰商户的支付受理与微信支付服务。'
    },
    lead: {
      en: 'Enring focuses on merchant payment acceptance, WeChat Pay enablement, settlement-related support and operating service for local and cross-border commerce. It was among the earliest New Zealand service providers supporting WeChat Pay for merchants.',
      zh: 'Enring 专注于商户支付受理、微信支付接入、结算相关支持以及本地与跨境商业的支付运营服务，是新西兰较早为商户提供微信支付服务的本地服务商之一。'
    },
    metaTitle: {
      en: 'Enring | New Zealand WeChat Pay and Merchant Payment Service',
      zh: 'Enring | 新西兰微信支付与商户支付服务'
    },
    metaDescription: {
      en: 'Enring provides merchant payment acceptance, WeChat Pay enablement, settlement-related support and payment operations in New Zealand.',
      zh: 'Enring 在新西兰提供商户支付受理、微信支付接入、结算相关支持与支付运营服务。'
    },
    cta: {
      en: 'Discuss payment service',
      zh: '咨询支付服务'
    },
    focus: {
      en: 'Payment capability for merchants.',
      zh: '面向商户的支付能力。'
    },
    features: [
      {
        icon: Store,
        title: { en: 'Merchant Services', zh: '商户服务' },
        body: {
          en: 'Support systems for merchant onboarding, service operations and commercial workflows.',
          zh: '支持商户入驻、服务运营与商业流程的系统能力。'
        }
      },
      {
        icon: ShieldCheck,
        title: { en: 'Settlement Support', zh: '结算支持' },
        body: {
          en: 'Infrastructure patterns that help businesses handle settlement-related processes with clarity.',
          zh: '帮助企业清晰处理结算相关流程的基础设施模式。'
        }
      },
      {
        icon: Globe2,
        title: { en: 'Cross-Border Commerce', zh: '跨境商业' },
        body: {
          en: 'Operational support for business activity that spans local and international markets.',
          zh: '面向本地与国际市场商业活动的运营支持。'
        }
      }
    ],
    proof: [
      {
        icon: Handshake,
        title: { en: 'Commerce support', zh: '商业支持' },
        body: { en: 'Service capability for everyday business operations.', zh: '支撑日常商业运营的服务能力。' }
      },
      {
        icon: Zap,
        title: { en: 'Operational technology', zh: '运营技术' },
        body: { en: 'Reusable technology patterns for merchant-facing workflows.', zh: '面向商户流程的可复用技术模式。' }
      },
      {
        icon: ArrowUpRight,
        title: { en: 'Growth foundation', zh: '增长基础' },
        body: { en: 'A practical base layer for the wider group platform.', zh: '集团平台体系的实用底层能力。' }
      }
    ]
  },
  minimall: {
    key: 'minimall',
    host: 'mmall.co.nz',
    name: 'MINI MALL',
    eyebrow: {
      en: 'Community Commerce Network',
      zh: '社区商业网络'
    },
    title: {
      en: 'Community-powered product distribution for trusted networks.',
      zh: '由社群驱动、基于信任网络的产品分销。'
    },
    lead: {
      en: 'MINI MALL enables merchants, agents, promoters and private-domain communities to distribute products through trusted social networks, cashback incentives and commission-based growth.',
      zh: 'MINI MALL 通过商户、代理、推广者与私域社群，结合返现激励与佣金机制，帮助产品在信任关系网络中实现销售增长。'
    },
    metaTitle: {
      en: 'MINI MALL | Community Commerce Network',
      zh: 'MINI MALL | 社区商业网络'
    },
    metaDescription: {
      en: 'MINI MALL connects merchants, agents, promoters and communities through cashback incentives and commission-based growth.',
      zh: 'MINI MALL 通过返现激励与佣金机制连接商户、代理、推广者与私域社群。'
    },
    cta: {
      en: 'Build with MINI MALL',
      zh: '接入 MINI MALL'
    },
    focus: {
      en: 'Designed for social distribution and merchant growth.',
      zh: '为社交分销与商户增长而设计。'
    },
    features: [
      {
        icon: UsersRound,
        title: { en: 'Agent Network', zh: '代理网络' },
        body: {
          en: 'A distributed growth layer for agents, promoters and community operators.',
          zh: '面向代理、推广者与社群运营者的分布式增长层。'
        }
      },
      {
        icon: WalletCards,
        title: { en: 'Cashback Programs', zh: '返现机制' },
        body: {
          en: 'Incentive structures that turn purchase activity into ongoing customer engagement.',
          zh: '将购买行为转化为持续用户参与的激励结构。'
        }
      },
      {
        icon: Store,
        title: { en: 'Merchant Growth', zh: '商户增长' },
        body: {
          en: 'Tools and network effects that help merchants reach trusted private-domain audiences.',
          zh: '帮助商户触达可信私域受众的工具与网络效应。'
        }
      }
    ],
    proof: [
      {
        icon: Network,
        title: { en: 'Private-domain reach', zh: '私域触达' },
        body: { en: 'Distribution through trusted social relationships.', zh: '通过可信社交关系完成分销。' }
      },
      {
        icon: Handshake,
        title: { en: 'Commission alignment', zh: '佣金协同' },
        body: { en: 'Growth incentives aligned across merchants and promoters.', zh: '让商户与推广者共享增长激励。' }
      },
      {
        icon: ArrowUpRight,
        title: { en: 'Repeatable growth', zh: '可复制增长' },
        body: { en: 'Community commerce that compounds with participation.', zh: '随着参与度累积的社区商业模式。' }
      }
    ]
  }
};

export const flywheel = [
  { en: 'Commerce Infrastructure', zh: '商业基础设施' },
  { en: 'Merchant Network', zh: '商户网络' },
  { en: 'Community Commerce', zh: '社区商业' },
  { en: 'Operational Data', zh: '运营数据' },
  { en: 'Operational Intelligence', zh: '运营智能' },
  { en: 'Automation', zh: '自动化' },
  { en: 'Business Growth', zh: '商业增长' }
];

export const contact = {
  title: {
    en: 'Work with MINI MALL GROUP.',
    zh: '与 MINI MALL GROUP 建立合作。'
  },
  body: {
    en: 'We welcome conversations with investors, enterprise customers, government agencies, strategic partners and future team members.',
    zh: '我们欢迎投资人、企业客户、政府机构、战略合作伙伴与潜在团队成员进行沟通。'
  },
  email: 'contact@mmallgroup.com'
};

export function resolveSiteKey(hostname: string, search: string): SiteKey {
  const params = new URLSearchParams(search);
  const siteParam = params.get('site') as SiteKey | null;

  if (siteParam && siteParam in sites) {
    return siteParam;
  }

  const host = hostname.toLowerCase();
  if (host === 'enring.co.nz' || host === 'www.enring.co.nz' || host.includes('enring.')) return 'enring';
  if (host === 'mmall.co.nz' || host === 'www.mmall.co.nz' || host.includes('mm.')) return 'minimall';
  return 'group';
}

export function localized(value: Localized, language: Language) {
  return value[language];
}
